import React, { createContext, useContext, useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useOrganisation from './useOrganisation';
import { useAuth } from '../../../components/navigation/Authentication';

const OrganisationContext = createContext();

export const useOrgContext = () => useContext(OrganisationContext);

export const OrganisationProvider = ({ children }) => {
    const { user } = useAuth(); // Get the authenticated user from the Auth context
    const [editingOrganisationId, setEditingOrganisationId] = useState(() => {
        return localStorage.getItem('editingOrganisationId') || null;
      });
    const [userOrganisation, setUserOrganisation] = useState(null);

    // Where a failed switch falls back to. null means the user's own organisation.
    const previousOrganisationIdRef = useRef(null);

    // Gets the organisation data. If editingOrganisationId is null, it fetches the user's own organisation.
    const { orgData: organisationData, loading, isFetched, refetch, error } = useOrganisation(editingOrganisationId);

    useEffect(() => {
        if (!user) return; // Early return if user is not authenticated

        if (!editingOrganisationId && organisationData && Object.keys(organisationData).length > 0 && !userOrganisation) {
            setUserOrganisation(organisationData);  // Store user's own org data
        }
    }, [user, editingOrganisationId, organisationData, userOrganisation]);
    

    // Function to change the active organisation
    const changeOrganisation = (newOrganisationID) => {
        previousOrganisationIdRef.current = editingOrganisationId;
        setEditingOrganisationId(newOrganisationID);
    };
    
    // Reset active organisation to the user's own organisation
    const resetOrganisation = () => {
        setEditingOrganisationId(null);
    };

    useEffect(() => {
        if (editingOrganisationId) {
          localStorage.setItem('editingOrganisationId', editingOrganisationId);
        } else {
          localStorage.removeItem('editingOrganisationId');
        }
      }, [editingOrganisationId]);

    // An id with no organisation behind it (deleted, or never existed) would otherwise sit in
    // localStorage until the next full login. Fall back to the previous organisation, then the user's own.
    // isFetched rather than loading: a query disabled before login also reads as not loading with no data.
    // Keyed on missing data, not on error: a failed background refetch keeps the cached organisation.
    const loadedOrganisationId = organisationData?.id;

    useEffect(() => {
        if (!user || !editingOrganisationId || !isFetched) return;

        if (!loadedOrganisationId) {
            setEditingOrganisationId(previousOrganisationIdRef.current);	// If previous is null, this will fall back to the user's own organisation
            previousOrganisationIdRef.current = null; // A second failure lands on the user's own organisation
        }
    }, [user, editingOrganisationId, isFetched, loadedOrganisationId]);

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        organisationData,
        userOrganisation,  // User's own organisation - could be different from the active organisation if the user is a super admin
        changeOrganisation,
        resetOrganisation,
        refetchOrganisation: refetch,
        loadingOrganisation: loading,
        organisationError: error ? (error.message || 'An unknown error occurred') : null
    }), [organisationData, userOrganisation, changeOrganisation, resetOrganisation, refetch, loading, error]);

    return (
        <OrganisationContext.Provider value={contextValue}>
            {children}
        </OrganisationContext.Provider>
    );
};

OrganisationProvider.propTypes = {
    children: PropTypes.node,
};
